import React, { useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Divider, Popover, Stack, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, TextField, Typography
} from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import httpClient, { extractErrorMessage } from '../Services/httpClient';

const currentPeriod = () => new Date().toISOString().slice(0, 7);

const formatDateTime = (timestamp) => timestamp ? new Date(timestamp).toLocaleString('pt-BR') : '';

// Extrato mensal como popover acionado por botão (ajuste pedido pelo usuário) — período no
// formato YYYY-MM, mesmo parsing de core-wallet WalletService.ParsePeriod.
const CoinStatementButton = () => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [period, setPeriod] = useState(currentPeriod());
    const [statement, setStatement] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [error, setError] = useState(null);

    const load = async (targetPeriod) => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await httpClient.get(`/backoffice/coin-statements/${targetPeriod}`);
            setStatement(response.data);
        } catch (err) {
            setError(extractErrorMessage(err));
            setStatement(null);
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpen = (event) => {
        setAnchorEl(event.currentTarget);
        load(period);
    };

    const handlePeriodChange = (value) => {
        setPeriod(value);
        if (value) load(value);
    };

    const handleClose = async () => {
        if (!window.confirm(`Fechar definitivamente o período ${period}? Essa ação não pode ser desfeita.`)) return;
        setIsClosing(true);
        setError(null);
        try {
            await httpClient.post(`/backoffice/coin-statements/${period}/close`);
            await load(period);
        } catch (err) {
            setError(extractErrorMessage(err));
        } finally {
            setIsClosing(false);
        }
    };

    return (
        <>
            <Button size="small" startIcon={<ReceiptLongIcon />} onClick={handleOpen} sx={{ color: '#f77513' }}>
                Extrato
            </Button>
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { width: 480, maxWidth: '92vw', maxHeight: '80vh', overflowY: 'auto', p: 2 } } }}
            >
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ color: '#f77513', fontWeight: 'bold' }}>
                        Extrato de Lemon Coins
                    </Typography>
                    <TextField
                        type="month"
                        size="small"
                        value={period}
                        onChange={(e) => handlePeriodChange(e.target.value)}
                    />
                </Stack>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {isLoading ? (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                        <CircularProgress size={24} style={{ color: '#f77513' }} />
                    </Box>
                ) : statement && (
                    <>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                            <Chip
                                label={statement.closed ? 'Fechado' : 'Aberto'}
                                size="small"
                                style={statement.closed ? { backgroundColor: '#e8f5e9', color: '#2e7d32' } : { backgroundColor: '#fff3e0', color: '#f9a825' }}
                            />
                            {statement.closed && (
                                <Typography variant="caption" color="text.secondary">
                                    por {statement.closedBy} em {formatDateTime(statement.closedAt)}
                                </Typography>
                            )}
                        </Stack>

                        <Typography variant="body2">
                            Total: <strong>{statement.totalQuantity} 🍋</strong> em {statement.transactionCount} lançamento(s), {statement.consultantCount} consultor(es)
                        </Typography>

                        {Object.keys(statement.quantityByReason || {}).length > 0 && (
                            <Box sx={{ mt: 1.5 }}>
                                <Typography variant="caption" color="text.secondary">Por motivo</Typography>
                                {Object.entries(statement.quantityByReason).map(([reason, quantity]) => (
                                    <Box key={reason} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2">{reason}</Typography>
                                        <Typography variant="body2">{quantity} 🍋</Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}

                        <Divider sx={{ my: 1.5 }} />

                        <TableContainer sx={{ maxHeight: 220 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Consultor</TableCell>
                                        <TableCell align="right">Qtde</TableCell>
                                        <TableCell align="right">Lanç.</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(statement.byConsultant || []).length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={3} align="center" style={{ color: '#aaa' }}>
                                                Nenhum lançamento neste período
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {(statement.byConsultant || []).map((line) => (
                                        <TableRow key={line.consultantId}>
                                            <TableCell style={{ fontFamily: 'monospace', fontSize: '11px' }}>{line.consultantId}</TableCell>
                                            <TableCell align="right">{line.totalQuantity} 🍋</TableCell>
                                            <TableCell align="right">{line.transactionCount}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {!statement.closed && (
                            <Box sx={{ mt: 2, textAlign: 'right' }}>
                                <Button
                                    size="small"
                                    variant="contained"
                                    disabled={isClosing}
                                    startIcon={isClosing ? <CircularProgress size={14} color="inherit" /> : null}
                                    style={{ backgroundColor: '#f77513', color: 'white' }}
                                    onClick={handleClose}
                                >
                                    Fechar período
                                </Button>
                            </Box>
                        )}
                    </>
                )}
            </Popover>
        </>
    );
};

export default CoinStatementButton;
